import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useLocalization } from '@experiences/locales';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    formatDate,
    getDateFromNow,
    useRegionDisplay,
} from '@experiences/util';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { makeStyles } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    MigrationEndWindowDays,
    MigrationStartWindowDays,
} from '../../../common/interfaces/gws';
import {
    generateLocalizedTimeWindows,
    getBrowserTimezone,
} from '../../../util/MigrationUtil';
import { MigrationScheduleSummary } from './MigrationScheduleSummary';
import { MigrationTimezoneAlert } from './migrationShared';
import type { IMigrationFormData } from './types';

interface MigrationStepConfirmProps {
    fixedWidth: string;
    i18n: {
        confirmTitle: string;
        sourceRegion: string;
        destinationRegion: string;
        downtimeWindow: string;
        dateWindow: string;
        whatsNext: string;
        confirmInfoKey: (index: number) => string;
    };
}

const useStyles = makeStyles((theme) =>
    ({
        header: { textAlign: 'center' },
        box: {
            outline: 'solid',
            outlineWidth: '1px',
            outlineColor: theme.palette.semantic.colorBorderDeEmp,
            borderRadius: '3px',
            padding: '16px 32px',
        },
    }),
);

// ts-unused-exports:disable-next-line
export const MigrationStepConfirm: React.FC<MigrationStepConfirmProps> = ({
    fixedWidth,
    i18n,
}) => {
    const classes = useStyles();
    const language = useLocalization();
    const { formatMessage: translate } = useIntl();
    const {
        getValues, watch,
    } = useFormContext<IMigrationFormData>();
    const getRegionDisplayValue = useRegionDisplay();
    const enableCustomSchedule = useFeatureFlagValue(Features.EnableCustomMigrationSchedule.name) as boolean;

    const hour = getValues('customHour') || getValues('hour');
    const scheduleMode = watch('scheduleMode') || 'earliest';
    const customDates = watch('customDates') || [];

    const windowStartDate = getDateFromNow(MigrationStartWindowDays);
    const windowEndDate = getDateFromNow(MigrationEndWindowDays);

    const tz = useMemo(getBrowserTimezone, []);
    const localizedWindows = useMemo(
        () => generateLocalizedTimeWindows(tz.offsetMinutes, tz.short, translate),
        [ tz, translate ],
    );
    const selectedTw = useMemo(
        () => localizedWindows.find(w => w.utcValue === hour) ?? localizedWindows[0],
        [ localizedWindows, hour ],
    );

    const timeDisplay = enableCustomSchedule
        ? selectedTw.localLabel
        : `${hour.split('-')[0] + ' UTC'} - ${hour.split('-')[1] + ' UTC'}`;

    return (
        <UiStack
            direction="column"
            gap={SpacingToken.M}>

            <UiText
                variant={FontVariantToken.fontSizeLBold}
                className={classes.header}>
                {translate({ id: i18n.confirmTitle })}
            </UiText>

            {enableCustomSchedule && <MigrationTimezoneAlert tz={tz} />}

            <UiStack
                className={classes.box}
                align='center'
                gap={SpacingToken.XL}>
                <UiStack
                    direction='column'
                    gap={SpacingToken.Micro}>
                    <UiText
                        style={{ width: fixedWidth }}
                        variant={FontVariantToken.fontSizeMBold}>
                        {translate({ id: i18n.sourceRegion })}
                    </UiText>
                    <UiText variant={FontVariantToken.fontSizeM}>
                        {getRegionDisplayValue(watch('sourceRegion'))}
                    </UiText>
                </UiStack>
                <ArrowRightAltIcon />
                <UiStack
                    direction='column'
                    gap={SpacingToken.Micro}>
                    <UiText variant={FontVariantToken.fontSizeMBold}>
                        {translate({ id: i18n.destinationRegion })}
                    </UiText>
                    <UiText variant={FontVariantToken.fontSizeM}>
                        {getRegionDisplayValue(watch('destinationRegion'))}
                    </UiText>
                </UiStack>
            </UiStack>

            <UiStack
                className={classes.box}
                align='center'
                gap={SpacingToken.XL}>
                <UiStack
                    direction='column'
                    gap={SpacingToken.Micro}>
                    <UiText
                        style={{ width: fixedWidth }}
                        variant={FontVariantToken.fontSizeMBold}>
                        {translate({ id: i18n.downtimeWindow })}
                    </UiText>
                    <UiText variant={FontVariantToken.fontSizeM}>
                        {timeDisplay}
                    </UiText>
                    {enableCustomSchedule && (
                        <UiText variant={FontVariantToken.fontSizeS}>
                            {selectedTw.utcLabel}
                        </UiText>
                    )}
                </UiStack>
                <ArrowRightAltIcon />
                <UiStack
                    direction='column'
                    gap={SpacingToken.Micro}>
                    {enableCustomSchedule && scheduleMode === 'flexible' && customDates.length > 0 ? (
                        <>
                            <UiText variant={FontVariantToken.fontSizeMBold}>
                                {translate({ id: 'CLIENT_MIGRATION_FLEXIBLE_SCHEDULE' })}
                            </UiText>
                            <MigrationScheduleSummary selectedDates={customDates} />
                        </>
                    ) : (
                        <>
                            <UiText variant={FontVariantToken.fontSizeMBold}>
                                {translate({ id: i18n.dateWindow })}
                            </UiText>
                            <UiText variant={FontVariantToken.fontSizeM}>
                                {formatDate(windowStartDate.toISOString(), language)}
                                &nbsp;-&nbsp;
                                {formatDate(windowEndDate.toISOString(), language)}
                            </UiText>
                        </>
                    )}
                </UiStack>
            </UiStack>

            <UiStack
                className={classes.box}
                direction='column'>
                <UiText variant={FontVariantToken.fontSizeMBold}>
                    {translate({ id: i18n.whatsNext })}
                </UiText>
                <ul>
                    {Array.from({ length: 3 }, (_, index) => (
                        <li key={index}>
                            <UiText display="inline">
                                {translate({ id: i18n.confirmInfoKey(index + 1) })}
                            </UiText>
                        </li>
                    ))}
                </ul>
            </UiStack>
        </UiStack>
    );
};
